import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useStaticPermission } from 'common/auth/Authorize';
import { MediaModel, CsvTemplatesModel as OldCsvTemplatesModel } from '@utmost.co/enterprise-models';
import { useCompare, Select, Button } from '@beeline.com/bp-design-system';
import { AddTemplate } from '../QuickStart/AddTemplate';
import { RemoveTemplate } from '../QuickStart/RemoveTemplate';
import { useImportersIntl } from './translations/useImportersIntl';
import { CsvTemplateSubTypeModel, CsvTemplateTypeModel, ImportEntityTypeModel } from 'src/graphql/types';
import { GetCsvTemplatesDocument, GetCsvTemplatesQuery } from './utils/__generated__/GetCsvTemplates.graphql';
import { useTypedQuery } from 'src/utils/hooks/useTypedQuery';
import { filterNotEmpty } from 'src/utils/data/listHelpers';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';

const getCsvType = (importType: CsvTemplateProps['importType']): CsvTemplateTypeModel | undefined => {
  if (importType?.id === 'CUSTOM_FIELD_OBJECT_VALUE') {
    return CsvTemplateTypeModel.CustomFieldObjectValue;
  }
  switch (importType?.entityType) {
    case ImportEntityTypeModel.Worker:
      return CsvTemplateTypeModel.Quickstart;
    case ImportEntityTypeModel.CostCenter:
      return CsvTemplateTypeModel.CostCenter;
    case ImportEntityTypeModel.Supplier:
      return CsvTemplateTypeModel.Supplier;
    case ImportEntityTypeModel.RateCard:
      return CsvTemplateTypeModel.RateCard;
    case ImportEntityTypeModel.Classification:
      return CsvTemplateTypeModel.Classification;
    case ImportEntityTypeModel.JobFamily:
      return CsvTemplateTypeModel.JobFamily;
    case ImportEntityTypeModel.JobFamilyGroup:
      return CsvTemplateTypeModel.JobFamilyGroup;
    case ImportEntityTypeModel.JobProfile:
      return CsvTemplateTypeModel.JobProfile;
    case ImportEntityTypeModel.JobApplication:
      return CsvTemplateTypeModel.JobApplication;
    case ImportEntityTypeModel.Expense:
      return CsvTemplateTypeModel.Expense;
    case ImportEntityTypeModel.BusinessSite:
      return CsvTemplateTypeModel.BusinessSite;
    case ImportEntityTypeModel.Organization:
      return CsvTemplateTypeModel.Organization;
    case ImportEntityTypeModel.Company:
      return CsvTemplateTypeModel.Company;
    case ImportEntityTypeModel.Contract:
      return CsvTemplateTypeModel.Contract;
    case ImportEntityTypeModel.Milestone:
      return CsvTemplateTypeModel.Milestone;
    case ImportEntityTypeModel.Timesheet:
      return CsvTemplateTypeModel.Timesheet;
    case ImportEntityTypeModel.Invoice:
      return CsvTemplateTypeModel.Invoice;
    case ImportEntityTypeModel.User:
      return CsvTemplateTypeModel.User;
    case ImportEntityTypeModel.CustomObject:
      return CsvTemplateTypeModel.CustomObject;
    case ImportEntityTypeModel.SpendApprovalLimit:
      return CsvTemplateTypeModel.SpendApprovalLimit;
    default:
      return undefined;
  }
};

interface CsvTemplateProps {
  importType:
    | {
        id: string;
        entityType: ImportEntityTypeModel;
      }
    | undefined;
}

export const CsvTemplateImporter: FC<React.PropsWithChildren<CsvTemplateProps>> = ({ importType }) => {
  const intl = useImportersIntl();
  const type = getCsvType(importType);
  const subType = importType?.id === 'WORKER-RETROACTIVE-CHANGE' ? CsvTemplateSubTypeModel.Retroactive : undefined;
  const csvPermission = useStaticPermission('CSV_TEMPLATE');
  const [media, setMedia] = useState<Pick<MediaModel, 'uid' | 'filename'> | undefined>();
  const [showAddTemplate, setShowAddTemplate] = useState<boolean>(false);
  const [showRemoveTemplate, setShowRemoveTemplate] = useState<boolean>(false);
  const { data, refetch, get: getCsvTemplates } = useTypedQuery(GetCsvTemplatesDocument);
  const { track } = useMixpanel();

  const handleSelect = (value?: Maybe<MediaModel>) => {
    track(MixpanelEvents.importerEvents.TEMPLATE_SELECT, { template: value?.filename });
    setMedia(value as MediaModel);
  };

  const options = useMemo(
    () =>
      filterNotEmpty((data as DeeplyDefined<GetCsvTemplatesQuery>)?.csvTemplates.items ?? []).map((item) => ({
        value: item.template,
        label: item.template?.filename ?? ''
      })) || [],
    [data]
  );

  const typeHasChanged = useCompare(type) && type;

  useEffect(() => {
    if (typeHasChanged) {
      getCsvTemplates({ variables: { type, subType } });
    }
  }, [typeHasChanged, type, subType, getCsvTemplates]);

  return (
    <>
      {!type && <StyledDiv>{intl.selectAnObject}</StyledDiv>}
      {type && (
        <StyledDownloadBox>
          {showAddTemplate && (
            <AddTemplate
              type={type}
              onClose={() => {
                refetch();
                setShowAddTemplate(false);
              }}
            />
          )}
          {showRemoveTemplate && data?.csvTemplates && (
            <RemoveTemplate
              onClose={() => {
                refetch();
                setShowRemoveTemplate(false);
              }}
              onRemove={() => {
                track(MixpanelEvents.importerEvents.TEMPLATE_REMOVE, { template: media?.filename });
                refetch();
              }}
              csvTemplates={data?.csvTemplates as OldCsvTemplatesModel}
            />
          )}
          <StyledTemplateContainer>
            <StyledSelectAndAnchor>
              <Select
                autoComplete
                dataTest="select_template"
                fullWidth
                id="select_template"
                menuProps={{
                  hideSelectedOptions: false,
                  sort: true
                }}
                onChange={handleSelect}
                options={options}
                placeholder={intl.selectTemplatePlaceholder}
                value={media}
              />

              <Button
                style={{ marginLeft: 8, pointerEvents: media ? 'initial' : 'none' }}
                href={`${window.location.origin}/api/media/${media?.uid}/content`}
                download={media?.filename}
                onClick={() => track(MixpanelEvents.importerEvents.TEMPLATE_DOWNLOAD, { template: media?.filename })}
                disabled={media === undefined}
                variant="outlined"
                color="default"
                dataTest="download-template"
                startIcon="download"
              />
            </StyledSelectAndAnchor>
            <StyledAddRemoveContainer>
              {csvPermission.CREATE && (
                <Button color="primary" onClick={() => setShowAddTemplate(true)} dataTest="add-template">
                  {intl.addTemplate}
                </Button>
              )}
              {csvPermission.CREATE && csvPermission.DELETE && `/`}
              {csvPermission.DELETE && (
                <Button color="primary" onClick={() => setShowRemoveTemplate(true)} dataTest="remove-template">
                  {intl.removeTemplate}
                </Button>
              )}
            </StyledAddRemoveContainer>
          </StyledTemplateContainer>
        </StyledDownloadBox>
      )}
    </>
  );
};

const StyledDownloadBox = styled.div`
  margin-top: 1rem;
`;

const StyledTemplateContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  [data-test='select_template'] {
    flex-grow: 2;
  }
`;

const StyledSelectAndAnchor = styled.div`
  display: flex;
  height: 40px;
  align-items: center;
`;

const StyledAddRemoveContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.8rem;
`;

const StyledDiv = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  padding: 2rem;
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
`;
